// src/redux/slidesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../api';
import { sampleSlide1, sampleSlide2, sampleSlide3 } from '../config/DefaultSlides';

// Thunk: Fetch slides
export const fetchSlides = createAsyncThunk(
  'slides/fetchSlides',
  async (deckId) => {
    if (deckId) {
      const response = await api.get(`/slides/${deckId}`);
      // if response.data.length === 0, return default slides
      if (response.data.length === 0) {
        return [sampleSlide1]; // Default slides
      }
      return response.data;
    } else {
      return [sampleSlide1]; // Default slide
    }
  }
);

// Thunk: Delete slide
export const deleteSlide = createAsyncThunk(
  'slides/deleteSlide',
  async ({ deckId, slideNumber }) => {
    const response = await api.delete(`/slides/${deckId}/${slideNumber}`);
    return { deckId, slideNumber };
  }
);

// Thunk: Save slide (JSON data)
export const saveSlide = createAsyncThunk(
  'slides/saveSlide',
  async (slideData, { rejectWithValue }) => {
    try {
      localStorage.setItem('googleReviewSlide', "no");
      const response = await api.post(`/slide`, slideData);
      console.log(`Updated slide response:`, response.data);
      if (response.data?.type === 'google_review') {
        localStorage.setItem('googleReviewSlide', "yes");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error saving slide');
    }
  }
);

// Thunk: Upload slide (multipart/form-data)
export const saveOtherSlide = createAsyncThunk(
  'slides/saveOtherSlide',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post(`/other-slide`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Multipart slide update response:', response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error uploading slide');
    }
  }
);

const slidesSlice = createSlice({
  name: 'slides',
  initialState: {
    slides: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch slides
      .addCase(fetchSlides.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSlides.fulfilled, (state, action) => {
        state.loading = false;
        state.slides = action.payload;
      })
      .addCase(fetchSlides.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Delete slide
      .addCase(deleteSlide.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSlide.fulfilled, (state, action) => {
        state.loading = false;
        state.slides = state.slides.filter(
          (slide) =>
            !(
              slide.deck_id === action.payload.deckId &&
              slide.slide_number === action.payload.slideNumber
            )
        );
      })
      .addCase(deleteSlide.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Save slide (JSON)
      .addCase(saveSlide.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveSlide.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.slides.findIndex(
          (slide) => slide.slide_id === action.payload.slide_id
        );
        if (index !== -1) {
          state.slides[index] = action.payload;
        } else {
          state.slides.push(action.payload);
        }
      })
      .addCase(saveSlide.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Upload slide (multipart/form-data)
      .addCase(saveOtherSlide.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveOtherSlide.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.slides.findIndex(
          (slide) => slide.slide_number === action.payload.slide_number
        );
        if (index >= 0) {
          state.slides[index] = action.payload;
        } else {
          state.slides.push(action.payload);
        }
      })
      .addCase(saveOtherSlide.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export default slidesSlice.reducer;
